/** `/catalog/brands`: Mode B approved brand list (D10) and per-brand product feeds with organizer approval. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BrandDto, ProductFeedItemDto } from '@windrop/contracts';
import { Button, Field, Input, useToast } from '@windrop/ui';
import { useMemo, useState } from 'react';
import { Can } from '../../auth/guards';
import { DataTable, type Column } from '../../components/DataTable';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { catalogApi } from './api';

export function BrandsPage() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const brands = useQuery({ queryKey: ['brands'], queryFn: () => catalogApi.brands.list() });
  const [selected, setSelected] = useState<string | undefined>();
  const feed = useQuery({
    queryKey: ['feed', selected],
    queryFn: () => catalogApi.feeds.list(selected),
    enabled: Boolean(selected),
  });
  const [name, setName] = useState('');
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['brands'] });
    void qc.invalidateQueries({ queryKey: ['feed'] });
  };
  const create = useMutation({
    mutationFn: () => catalogApi.brands.create({ name }),
    onSuccess: () => {
      setName('');
      refresh();
      toast({ title: 'Brand added — approve it to use it in value-cap prizes' });
    },
  });
  const approve = useMutation({
    mutationFn: (b: BrandDto) => catalogApi.brands.approve(b.brandId, { approved: !b.approved }),
    onSuccess: refresh,
  });
  const approveItem = useMutation({
    mutationFn: (i: ProductFeedItemDto) => catalogApi.feeds.approve(i.feedItemId, { approved: !i.approved }),
    onSuccess: refresh,
  });
  const columns = useMemo<Column<BrandDto>[]>(
    () => [
      {
        id: 'name',
        header: 'Brand',
        sortValue: (r) => r.name.toLowerCase(),
        cell: (r) => (
          <button type="button" className="wd-link" onClick={() => setSelected(r.brandId)}>
            {r.name}
          </button>
        ),
      },
      { id: 'approved', header: 'Approval', cell: (r) => (r.approved ? `Approved` : 'Not approved') },
      { id: 'logo', header: 'Logo', cell: (r) => (r.logo ? `${r.logo.width}×${r.logo.height}` : 'Missing') },
    ],
    [],
  );
  return (
    <>
      <PageHeader
        title="Approved brands"
        breadcrumb={[{ label: 'Sponsors & prizes', to: '/catalog' }, { label: 'Brands' }]}
        description="Mode B winners choose within the value cap from these brands only; the API enforces the list (§6.2)."
      />
      <Can permission="brands.manage">
        <div style={{ display: 'flex', gap: 8, alignItems: 'end' }}>
          <Field label="New brand">
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="brand-name" />
          </Field>
          <Button
            variant="secondary"
            disabled={!name.trim()}
            loading={create.isPending}
            onClick={() => create.mutate()}
          >
            Add brand
          </Button>
        </div>
      </Can>
      <DataTable
        caption="Brands"
        columns={columns}
        rows={brands.data ?? []}
        rowKey={(r) => r.brandId}
        loading={brands.isPending}
        rowActions={(r) => (
          <Can permission="brands.manage">
            <Button variant="quiet" onClick={() => approve.mutate(r)}>
              {r.approved ? 'Revoke' : 'Approve'}
            </Button>
          </Can>
        )}
        emptyState={
          <EmptyState title="No brands" body="Add the brands winners may choose from." action={<span />} />
        }
      />
      {selected ? (
        <>
          <h2>Product feed</h2>
          <table className="wdc-table">
            <thead>
              <tr>
                <th>SKU</th>
                <th>Product</th>
                <th>Retail value</th>
                <th>Stock</th>
                <th>Approved</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(feed.data ?? []).map((i) => (
                <tr key={i.feedItemId}>
                  <td>
                    <code>{i.externalSku}</code>
                  </td>
                  <td>{i.name}</td>
                  <td>
                    {i.retailValue.currency} {i.retailValue.amountMinor}
                  </td>
                  <td>{i.stock}</td>
                  <td>{i.approved ? 'Yes' : 'No'}</td>
                  <td>
                    <Can permission="brands.manage">
                      <Button variant="quiet" onClick={() => approveItem.mutate(i)}>
                        {i.approved ? 'Revoke' : 'Approve'}
                      </Button>
                    </Can>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : null}
    </>
  );
}
